<template>
  <div v-if="show" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-fade-in">
    <div class="bg-white dark:bg-slate-900 w-full max-w-6xl rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col max-h-[95vh]">

      <!-- Encabezado -->
      <div class="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50/50 dark:bg-slate-800/30">
        <div>
          <h2 class="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Store class="w-5 h-5 text-[#F36421]" />
            {{ form.id ? 'Editar Comercio / Cliente' : 'Registro de Comercio B2B o Cliente B2C' }}
          </h2>
          <p class="text-xs text-slate-500">Gestión de perfil, rubro comercial, documentación tributaria y libreta de sucursales.</p>
        </div>
        <button @click="$emit('close')" class="p-2 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-full transition-colors text-slate-500">
          <X class="w-5 h-5" />
        </button>
      </div>

      <!-- Pestañas -->
      <div class="flex overflow-x-auto bg-slate-50 dark:bg-slate-800/50 border-b border-slate-100 dark:border-slate-800">
        <button v-for="tab in tabs" :key="tab.id" @click="activeTab = tab.id"
          :class="['px-6 py-3 text-xs font-bold uppercase tracking-wider whitespace-nowrap transition-all border-b-2',
          activeTab === tab.id ? 'border-[#F36421] text-[#F36421] bg-white dark:bg-slate-900' : 'border-transparent text-slate-500 hover:text-slate-700']">
          {{ tab.name }}
          <span v-if="tab.id === 'addresses'" class="ml-1.5 px-2 py-0.5 rounded-full bg-slate-200 dark:bg-slate-700 text-[10px]">
            {{ form.addresses.length }}
          </span>
        </button>
      </div>

      <!-- Cuerpo del Formulario -->
      <div class="flex-1 overflow-y-auto p-6 custom-scrollbar">
        <form id="customerForm" @submit.prevent="handleSubmit" class="space-y-6" novalidate>

          <!-- ================================================================= -->
          <!-- PESTAÑA 1: PERFIL GENERAL & RUBRO COMERCIAL -->
          <!-- ================================================================= -->
          <div v-show="activeTab === 'general'" class="space-y-6 animate-fade-in">
            <div :class="sectionCardClasses">
              <label :class="labelClasses">* Tipo de Perfil en Sistema</label>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
                <label @click="form.customer_type = 'B2B_BUSINESS'"
                  :class="['flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all',
                  form.customer_type === 'B2B_BUSINESS' ? 'border-[#F36421] bg-orange-50/50 dark:bg-orange-950/10' : 'border-slate-200 dark:border-slate-700']">
                  <input type="radio" v-model="form.customer_type" value="B2B_BUSINESS" class="text-[#F36421] focus:ring-[#F36421]">
                  <div>
                    <div class="font-bold text-sm text-slate-900 dark:text-white">Comercio / Restaurante (B2B)</div>
                    <div class="text-xs text-slate-500">Envía pedidos a sus clientes. Aplica 0% comisión y facturación quincenal.</div>
                  </div>
                </label>

                <label @click="form.customer_type = 'B2C_PARTICULAR'"
                  :class="['flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all',
                  form.customer_type === 'B2C_PARTICULAR' ? 'border-[#F36421] bg-orange-50/50 dark:bg-orange-950/10' : 'border-slate-200 dark:border-slate-700']">
                  <input type="radio" v-model="form.customer_type" value="B2C_PARTICULAR" class="text-[#F36421] focus:ring-[#F36421]">
                  <div>
                    <div class="font-bold text-sm text-slate-900 dark:text-white">Cliente Particular (B2C)</div>
                    <div class="text-xs text-slate-500">Ciudadano que solicita mensajería, compras o envíos inmediatos.</div>
                  </div>
                </label>
              </div>
            </div>

            <!-- Foto o Logo -->
            <div :class="sectionCardClasses" class="flex flex-col md:flex-row items-center gap-6">
              <div class="relative group">
                <div class="w-28 h-28 rounded-2xl border-4 border-white dark:border-slate-800 shadow-xl overflow-hidden bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                  <img v-if="form.photo_url" :src="form.photo_url" class="w-full h-full object-cover" />
                  <Store v-else-if="form.customer_type === 'B2B_BUSINESS'" class="w-12 h-12 text-slate-400" />
                  <User v-else class="w-12 h-12 text-slate-400" />
                  <div v-if="isUploadingPhoto" class="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <Loader2 class="w-8 h-8 animate-spin text-white" />
                  </div>
                </div>
                <label class="absolute bottom-0 right-0 p-2 bg-[#F36421] text-white rounded-full cursor-pointer shadow-lg hover:bg-[#d95214] transition-all">
                  <Camera class="w-4 h-4" />
                  <input type="file" class="hidden" accept="image/*" @change="handleImageUpload($event, 'photo_url')" />
                </label>
              </div>
              <div class="text-center md:text-left">
                <h3 class="font-bold text-slate-800 dark:text-white">
                  {{ form.customer_type === 'B2B_BUSINESS' ? 'Logo del Comercio / Marca' : 'Fotografía del Cliente' }}
                </h3>
                <p class="text-xs text-slate-500">Se mostrará en la central de despacho y guías de envío.</p>
              </div>
            </div>

            <!-- Nombres, Rubro Comercial y Contacto -->
            <div :class="sectionCardClasses">
              <h3 :class="sectionTitleClasses">Información de Contacto & Rubro Comercial</h3>
              <div class="grid grid-cols-1 md:grid-cols-3 gap-4">

                <div v-if="form.customer_type === 'B2B_BUSINESS'" class="md:col-span-2">
                  <label :class="labelClasses">* Nombre Comercial / Marca</label>
                  <input v-model="form.business_name" type="text" :class="inputClasses" placeholder="Ej: Burger King Altamira" />
                </div>

                <div v-if="form.customer_type === 'B2B_BUSINESS'">
                  <label :class="labelClasses">* ¿A qué se dedica el negocio? (Rubro)</label>
                  <select v-model="form.business_sector" :class="inputClasses" required>
                    <option value="" disabled>Selecciona el rubro...</option>
                    <option value="Alimentos y Comida Rápida">🍔 Alimentos y Comida Rápida</option>
                    <option value="Pastelería y Repostería">🎂 Pastelería y Repostería</option>
                    <option value="Farmacia y Salud">💊 Farmacia y Salud</option>
                    <option value="Tienda de Ropa y Calzado">👗 Tienda de Ropa y Calzado</option>
                    <option value="Repuestos y Ferretería">🔧 Repuestos y Ferretería</option>
                    <option value="E-commerce y Variedades">📦 E-commerce y Variedades</option>
                    <option value="Documentos y Mensajería Legal">📄 Documentos y Mensajería Legal</option>
                    <option value="Otro Rubro Comercial">🏢 Otro Rubro Comercial</option>
                  </select>
                </div>

                <div><label :class="labelClasses">* Nombres del Responsable / Cliente</label><input v-model="form.first_name" type="text" :class="inputClasses" required /></div>
                <div><label :class="labelClasses">* Apellidos</label><input v-model="form.last_name" type="text" :class="inputClasses" required /></div>
                <div><label :class="labelClasses">* WhatsApp de Despacho (Principal)</label><input v-model="form.mobile_phone" type="text" :class="inputClasses" placeholder="8888-8888" required /></div>
                <div><label :class="labelClasses">Teléfono Fijo / Local</label><input v-model="form.work_phone" type="text" :class="inputClasses" placeholder="2222-2222" /></div>
                <div class="md:col-span-2"><label :class="labelClasses">Correo Electrónico (Facturas DTE)</label><input v-model="form.email" type="email" :class="inputClasses" placeholder="contacto@negocio.com" /></div>
              </div>
            </div>
          </div>

          <!-- ================================================================= -->
          <!-- PESTAÑA 2: DOCUMENTACIÓN TRIBUTARIA (RUC / CÉDULA) -->
          <!-- ================================================================= -->
          <div v-show="activeTab === 'id'" class="space-y-6 animate-fade-in">
            <div :class="sectionCardClasses">
              <h3 :class="sectionTitleClasses">
                <CreditCard class="w-4 h-4 text-[#F36421]" />
                {{ form.customer_type === 'B2B_BUSINESS' ? 'Registro RUC / Tributario' : 'Cédula de Identidad' }}
              </h3>
              <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label :class="labelClasses">* No. RUC / Cédula</label>
                  <input v-model="form.identity_document" type="text" :class="inputClasses" placeholder="000-000000-0000X o J00000000" required />
                </div>
                <div>
                  <label :class="labelClasses">¿Emite Factura Electrónica (DTE)?</label>
                  <select v-model="form.is_taxpayer" :class="inputClasses">
                    <option :value="true">SÍ (Contribuyente Formal)</option>
                    <option :value="false">NO (Régimen Simplificado / Particular)</option>
                  </select>
                </div>
                <div v-if="form.is_taxpayer">
                  <label :class="labelClasses">No. NRC / Registro</label>
                  <input v-model="form.tax_registration_number" type="text" :class="inputClasses" placeholder="NRC-12345" />
                </div>
              </div>

              <!-- Fotos de RUC o Cédula -->
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                <div @click="$refs.frontInput.click()" class="relative border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-xl h-40 flex flex-col items-center justify-center cursor-pointer hover:bg-slate-50 transition-all overflow-hidden bg-white dark:bg-slate-800">
                  <img v-if="form.document_front_url" :src="form.document_front_url" class="w-full h-full object-contain" />
                  <template v-else>
                    <Upload class="w-6 h-6 text-slate-400 mb-1" />
                    <span class="text-[10px] font-bold text-slate-500 uppercase">Subir RUC / Cédula (Frente)</span>
                  </template>
                  <input type="file" ref="frontInput" class="hidden" accept="image/*" @change="handleImageUpload($event, 'document_front_url')" />
                </div>
                <div @click="$refs.backInput.click()" class="relative border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-xl h-40 flex flex-col items-center justify-center cursor-pointer hover:bg-slate-50 transition-all overflow-hidden bg-white dark:bg-slate-800">
                  <img v-if="form.document_back_url" :src="form.document_back_url" class="w-full h-full object-contain" />
                  <template v-else>
                    <Upload class="w-6 h-6 text-slate-400 mb-1" />
                    <span class="text-[10px] font-bold text-slate-500 uppercase">Subir Reverso (Opcional)</span>
                  </template>
                  <input type="file" ref="backInput" class="hidden" accept="image/*" @change="handleImageUpload($event, 'document_back_url')" />
                </div>
              </div>
            </div>
          </div>

          <!-- ================================================================= -->
          <!-- PESTAÑA 3: LIBRETA DE SUCURSALES & DIRECCIONES -->
          <!-- ================================================================= -->
          <div v-show="activeTab === 'addresses'" class="space-y-6 animate-fade-in">
            <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-slate-50 dark:bg-slate-800 p-4 rounded-xl border border-slate-200 dark:border-slate-700">
              <div>
                <h3 class="font-bold text-slate-900 dark:text-white flex items-center gap-2">
                  <MapPin class="w-5 h-5 text-[#F36421]" />
                  Sucursales / Libreta de Direcciones
                </h3>
                <p class="text-xs text-slate-500">Agrega todas las sucursales del negocio o direcciones frecuentes del cliente.</p>
              </div>
              <button type="button" @click="openAddressModal()" class="px-4 py-2 bg-[#F36421] hover:bg-[#d95214] text-white text-xs font-bold rounded-lg shadow transition-all flex items-center gap-1.5">
                <Plus class="w-4 h-4" /> Agregar Sucursal / Dirección
              </button>
            </div>

            <div v-if="form.addresses.length === 0" class="text-center py-12 border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-2xl">
              <MapPin class="w-10 h-10 text-slate-300 mx-auto mb-2" />
              <p class="text-sm font-semibold text-slate-600 dark:text-slate-400">No hay sucursales o direcciones guardadas.</p>
              <p class="text-xs text-slate-400 mt-1">Haz clic en "Agregar Sucursal / Dirección" para registrar su punto de despacho.</p>
            </div>

            <div v-else class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div v-for="(addr, idx) in form.addresses" :key="idx"
                :class="['p-4 rounded-xl border transition-all relative group',
                addr.is_default ? 'border-[#F36421] bg-orange-50/30 dark:bg-orange-950/10' : 'border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-800']">

                <div class="flex justify-between items-start mb-2">
                  <span class="px-2.5 py-0.5 rounded-md text-xs font-bold bg-[#142032] text-white">
                    {{ addr.alias }}
                  </span>
                  <div class="flex items-center gap-1">
                    <button type="button" @click="setDefaultAddress(idx)" v-if="!addr.is_default"
                      class="text-[10px] text-slate-400 hover:text-[#F36421] font-semibold underline pr-2">
                      Hacer principal
                    </button>
                    <span v-else class="text-[10px] font-bold text-[#F36421] bg-orange-100 dark:bg-orange-900/40 px-2 py-0.5 rounded">
                      ★ Principal
                    </span>
                    <button type="button" @click="openAddressModal(addr, idx)" class="p-1.5 text-slate-400 hover:text-blue-600 rounded">
                      <Edit2 class="w-4 h-4" />
                    </button>
                    <button type="button" @click="removeAddress(idx)" class="p-1.5 text-slate-400 hover:text-red-600 rounded">
                      <Trash2 class="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div class="space-y-1 text-xs">
                  <p class="font-semibold text-slate-800 dark:text-slate-200">{{ addr.address }}</p>
                  <p v-if="addr.reference_address" class="text-slate-500 italic">"{{ addr.reference_address }}"</p>
                  <div class="flex items-center gap-3 pt-2 text-slate-400 font-mono">
                    <span v-if="addr.contact_phone">📞 {{ addr.contact_phone }}</span>
                    <span v-if="addr.latitude && addr.longitude" class="text-emerald-600 font-bold">📍 {{ addr.latitude }}, {{ addr.longitude }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- ================================================================= -->
          <!-- PESTAÑA 4: ACUERDOS LLEVO & CRÉDITO -->
          <!-- ================================================================= -->
          <div v-show="activeTab === 'commercial'" class="space-y-6 animate-fade-in">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div :class="sectionCardClasses">
                <h3 :class="sectionTitleClasses">Condiciones Logísticas</h3>
                <div class="space-y-4">
                  <div>
                    <label :class="labelClasses">Comisión por Venta de Productos</label>
                    <div class="flex items-center gap-2">
                      <input v-model="form.commission_rate" type="number" step="0.01" readonly class="w-24 px-3 py-2 bg-slate-100 dark:bg-slate-800 border rounded-lg font-bold text-emerald-600" />
                      <span class="text-xs font-bold text-slate-500">% (Beneficio 0.00% LLEVO)</span>
                    </div>
                  </div>
                  <div>
                    <label :class="labelClasses">Límite de Crédito para Fletes B2B (C$)</label>
                    <input v-model="form.credit_limit" type="number" step="100" :class="inputClasses" placeholder="0.00" />
                    <p class="text-[10px] text-slate-400 mt-1">Permite al comercio acumular sus envíos para pago quincenal/mensual.</p>
                  </div>
                </div>
              </div>

              <div :class="sectionCardClasses">
                <h3 :class="sectionTitleClasses"><PenTool class="w-4 h-4 text-[#F36421]" /> Firma del Cliente / Responsable</h3>
                <p class="text-[10px] text-slate-500 mb-2">Firma digital de acuerdo y términos logísticos de despacho.</p>
                <div class="border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-xl overflow-hidden bg-white shadow-inner">
                  <canvas id="signature-pad" class="w-full h-36 touch-none cursor-crosshair"></canvas>
                </div>
                <div class="mt-3 flex justify-end">
                  <button type="button" @click="clearSignature" class="text-xs font-bold text-red-500 hover:text-red-700 bg-red-50 px-3 py-1.5 rounded-lg">
                    Borrar y firmar de nuevo
                  </button>
                </div>
              </div>
            </div>
          </div>

        </form>
      </div>

      <!-- Pie de Modal Principal -->
      <div class="px-6 py-4 bg-white dark:bg-slate-900 border-t border-slate-100 dark:border-slate-800 flex justify-between items-center z-20">
        <div class="flex gap-3 w-full sm:w-auto justify-end ml-auto">
          <button @click="$emit('close')" type="button" :class="btnSecondary">Cancelar</button>
          <button v-if="activeTab !== 'commercial'" @click="nextTab" type="button" :class="btnPrimary">Siguiente</button>
          <button v-else type="submit" form="customerForm" :disabled="isSaving" :class="btnPrimary">
            <Loader2 v-if="isSaving" class="w-4 h-4 animate-spin" />
            <Save v-else class="w-4 h-4" />
            Guardar Expediente
          </button>
        </div>
      </div>

    </div>

    <!-- ================================================================= -->
    <!-- SUB-MODAL 1: AGREGAR / EDITAR SUCURSAL -->
    <!-- ================================================================= -->
    <div v-if="showAddrModal" class="fixed inset-0 z-60 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
      <div class="bg-white dark:bg-slate-900 w-full max-w-lg rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden">
        <div class="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center">
          <h3 class="font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <MapPin class="w-4 h-4 text-[#F36421]" />
            {{ editingAddrIndex !== null ? 'Editar Sucursal / Dirección' : 'Nueva Sucursal / Dirección' }}
          </h3>
          <button @click="showAddrModal = false" class="text-slate-400 hover:text-slate-600"><X class="w-5 h-5" /></button>
        </div>

        <div class="p-6 space-y-4 max-h-[80vh] overflow-y-auto">
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label :class="labelClasses">* Alias / Nombre</label>
              <input v-model="addrForm.alias" type="text" :class="inputClasses" placeholder="Ej: Sucursal Altamira / Casa" required />
            </div>
            <div>
              <label :class="labelClasses">Teléfono / WhatsApp de Sucursal</label>
              <input v-model="addrForm.contact_phone" type="text" :class="inputClasses" placeholder="8888-8888" />
            </div>
          </div>

          <div>
            <label :class="labelClasses">* Dirección Exacta</label>
            <input v-model="addrForm.address" type="text" :class="inputClasses" placeholder="Calle principal, número..." required />
          </div>

          <div>
            <label :class="labelClasses">* Señas Nicaragüenses</label>
            <input v-model="addrForm.reference_address" type="text" :class="inputClasses" placeholder="Ej: De la Pepsi 2c al lago, media arriba..." />
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label :class="labelClasses">Latitud GPS</label>
              <input v-model="addrForm.latitude" type="number" step="0.00000001" :class="inputClasses" placeholder="12.13282" />
            </div>
            <div>
              <label :class="labelClasses">Longitud GPS</label>
              <input v-model="addrForm.longitude" type="number" step="0.00000001" :class="inputClasses" placeholder="-86.25041" />
            </div>
          </div>

          <!-- Botones de GPS Automático y Mapa Leaflet -->
          <div class="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 pt-2">
            <div class="flex flex-wrap items-center gap-2">
              <!-- Botón Automático -->
              <button
                type="button"
                @click="captureAddrGPS"
                :disabled="isCapturingGPS"
                class="px-3 py-2 bg-blue-50 text-blue-600 text-xs font-bold rounded-lg hover:bg-blue-100 flex items-center gap-1.5 disabled:opacity-60 disabled:cursor-not-allowed transition-all"
              >
                <Loader2 v-if="isCapturingGPS" class="w-4 h-4 animate-spin text-blue-600" />
                <MapPin v-else class="w-4 h-4" />
                <span>{{ isCapturingGPS ? 'Triangulando...' : 'GPS Automático' }}</span>
              </button>

              <!-- Botón MAPA LEAFLET -->
              <button
                type="button"
                @click="openMapPicker"
                class="px-3 py-2 bg-orange-50 text-[#F36421] text-xs font-bold rounded-lg hover:bg-orange-100 flex items-center gap-1.5 transition-all border border-[#F36421]/20"
              >
                <span>🗺️ Ubicar en el Mapa</span>
              </button>
            </div>

            <label class="inline-flex items-center cursor-pointer">
              <input type="checkbox" v-model="addrForm.is_default" class="sr-only peer">
              <div :class="switchClasses"></div>
              <span class="ml-2 text-xs font-bold text-slate-700 dark:text-slate-300">¿Es Principal?</span>
            </label>
          </div>
        </div>

        <div class="px-6 py-4 bg-slate-50 dark:bg-slate-800 border-t flex justify-end gap-2">
          <button @click="showAddrModal = false" type="button" :class="btnSecondary">Cancelar</button>
          <button @click="saveAddress" type="button" :class="btnPrimary">Guardar Dirección</button>
        </div>
      </div>
    </div>

    <!-- ================================================================= -->
    <!-- SUB-MODAL 2: MAPA INTERACTIVO LEAFLET -->
    <!-- ================================================================= -->
    <div v-if="showMapModal" class="fixed inset-0 z-70 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fade-in">
      <div class="bg-white dark:bg-slate-900 w-full max-w-3xl rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden flex flex-col">

        <div class="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center">
          <div>
            <h3 class="font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <span>🗺️ Seleccionar Ubicación en el Mapa</span>
            </h3>
            <p class="text-xs text-slate-500">Haz clic sobre el mapa para colocar el pin de despacho.</p>
          </div>
          <button @click="closeMapPicker" class="text-slate-400 hover:text-slate-600"><X class="w-5 h-5" /></button>
        </div>

        <!-- Contenedor del Mapa Leaflet -->
        <div class="relative w-full h-[400px] bg-slate-100 dark:bg-slate-800">
          <div id="leaflet-map-container" class="w-full h-full z-10"></div>

          <!-- Banner inferior flotante con la coordenada elegida -->
          <div class="absolute bottom-3 left-3 right-3 z-20 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md p-3 rounded-xl border border-slate-200 dark:border-slate-700 shadow-lg flex justify-between items-center">
            <div class="text-xs">
              <span class="font-bold text-slate-500 uppercase block">Coordenada Seleccionada:</span>
              <span v-if="tempLat && tempLng" class="font-mono font-bold text-emerald-600 text-sm">
                {{ tempLat }}, {{ tempLng }}
              </span>
              <span v-else class="text-slate-400 italic">Haz clic en la calle exacta del mapa...</span>
            </div>
            <button
              type="button"
              @click="confirmMapLocation"
              :disabled="!tempLat || !tempLng"
              class="px-4 py-2 bg-[#F36421] hover:bg-[#d95214] disabled:opacity-50 text-white text-xs font-bold rounded-lg shadow transition-all"
            >
              Confirmar Ubicación
            </button>
          </div>
        </div>

      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, nextTick, onMounted } from 'vue';
import {
  X, Save, Loader2, UserPlus, CreditCard,
  MapPin, Phone, PenTool, Home, Briefcase, Camera, User, Upload, Search, Store, Plus, Trash2, Edit2
} from 'lucide-vue-next';
import SignaturePad from 'signature_pad';
import { useToast } from '@/composables/useToast';
import apiClient from '@/api/client';
import { SecureStorage } from '@/utils/secureStorage';
import { useUpload } from '@/composables/useUpload';
import { useSerials } from '@/composables/useSerials';

// IMPORTACIONES DE LEAFLET
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const { uploadFile, isUploading } = useUpload();
const { generateCode } = useSerials();
const isUploadingPhoto = ref(false);
const isUploadingFront = ref(false);
const isUploadingBack = ref(false);

const props = defineProps({ show: Boolean, applicationToEdit: Object });
const emit = defineEmits(['close', 'saved']);
const { showSuccess, showError } = useToast();

const activeTab = ref('general');
const isSaving = ref(false);
const signaturePad = ref(null);
const isCapturingGPS = ref(false);

const deletedAddressIds = ref([]);
const showAddrModal = ref(false);
const editingAddrIndex = ref(null);

// ============================================================================
// ESTADOS PARA EL MAPA INTERACTIVO LEAFLET
// ============================================================================
const showMapModal = ref(false);
const tempLat = ref(null);
const tempLng = ref(null);
let mapInstance = null;
let markerInstance = null;

// Ícono personalizado HTML para evitar errores de PNG en Webpack/Vite
const customPinIcon = L.divIcon({
  className: 'custom-leaflet-pin',
  html: `<div style="font-size: 32px; line-height: 1; transform: translate(-50%, -100%); text-shadow: 0 2px 4px rgba(0,0,0,0.3);">📍</div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 32]
});

const addrForm = reactive({
  id: null,
  alias: '',
  contact_phone: '',
  address: '',
  reference_address: '',
  latitude: null,
  longitude: null,
  is_default: false
});

const tabs = [
  { id: 'general', name: '1. Perfil & Contacto' },
  { id: 'id', name: '2. RUC / Cédula' },
  { id: 'addresses', name: '3. Sucursales & Direcciones' },
  { id: 'commercial', name: '4. Acuerdos & Crédito' }
];

const inputClasses = "w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#F36421]/20 focus:border-[#F36421] transition-all text-slate-700 dark:text-slate-200";
const labelClasses = "text-[10px] font-bold text-slate-500 uppercase mb-1 block";
const sectionCardClasses = "bg-slate-50/50 dark:bg-slate-800/30 p-5 rounded-xl border border-slate-100 dark:border-slate-800";
const sectionTitleClasses = "text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2 mb-4 uppercase tracking-tight";
const switchClasses = "relative w-9 h-5 bg-slate-300 rounded-full peer dark:bg-slate-700 peer-checked:bg-[#F36421] after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-full";
const btnPrimary = "px-6 py-2.5 text-sm font-bold text-white bg-[#F36421] rounded-xl hover:bg-[#d95214] shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-70 w-full sm:w-auto";
const btnSecondary = "px-4 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 transition-colors w-full sm:w-auto";

const form = reactive({
  id: null,
  customer_type: 'B2B_BUSINESS',
  business_name: '',
  business_sector: '',
  first_name: '', last_name: '', mobile_phone: '', work_phone: '', email: '',
  identity_document: '', is_taxpayer: false, tax_registration_number: '',
  photo_url: '', document_front_url: '', document_back_url: '',
  addresses: [],
  commission_rate: 0.00, credit_limit: 0.00,
  signature_base64: null
});

// Subida de Imágenes
const handleImageUpload = async (event, field) => {
  const file = event.target.files[0];
  if (!file) return;
  if (field === 'photo_url') isUploadingPhoto.value = true;
  try {
    const url = await uploadFile(file);
    if (url) {
      form[field] = url;
      showSuccess('Fotografía guardada con éxito');
    }
  } finally {
    if (field === 'photo_url') isUploadingPhoto.value = false;
  }
};

// ============================================================================
// FUNCIONES DEL MAPA LEAFLET INTERACTIVO
// ============================================================================
const openMapPicker = () => {
  // 1. Usar coordenada previa del form, o si está vacía centrar en Managua, Nicaragua
  const initialLat = addrForm.latitude || 12.136389;
  const initialLng = addrForm.longitude || -86.251389;

  tempLat.value = addrForm.latitude ? Number(addrForm.latitude) : null;
  tempLng.value = addrForm.longitude ? Number(addrForm.longitude) : null;

  showMapModal.value = true;

  nextTick(() => {
    // Si ya existe un mapa viejo, destruirlo para iniciarlo limpio
    if (mapInstance) {
      mapInstance.remove();
      mapInstance = null;
      markerInstance = null;
    }

    // Inicializar el mapa de Leaflet
    mapInstance = L.map('leaflet-map-container').setView([initialLat, initialLng], 14);

    // Agregar capa de OpenStreetMap
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 19
    }).addTo(mapInstance);

    // Si ya había una coordenada previa en el input, dibujar el pin inicial
    if (addrForm.latitude && addrForm.longitude) {
      markerInstance = L.marker([initialLat, initialLng], { icon: customPinIcon }).addTo(mapInstance);
    }

    // Evento CLICK en el mapa: mover/crear marcador y guardar lat/lng en tiempo real
    mapInstance.on('click', (e) => {
      const lat = Number(e.latlng.lat.toFixed(8));
      const lng = Number(e.latlng.lng.toFixed(8));

      tempLat.value = lat;
      tempLng.value = lng;

      if (markerInstance) {
        markerInstance.setLatLng([lat, lng]);
      } else {
        markerInstance = L.marker([lat, lng], { icon: customPinIcon }).addTo(mapInstance);
      }
    });

    // Refrescar tamaño por si el modal tardó en abrirse en el DOM
    setTimeout(() => {
      mapInstance.invalidateSize();
    }, 200);
  });
};

const closeMapPicker = () => {
  if (mapInstance) {
    mapInstance.remove();
    mapInstance = null;
    markerInstance = null;
  }
  showMapModal.value = false;
};

const confirmMapLocation = () => {
  if (tempLat.value && tempLng.value) {
    addrForm.latitude = tempLat.value;
    addrForm.longitude = tempLng.value;
    addrForm.gps_coordinates = `${tempLat.value}, ${tempLng.value}`;
    showSuccess('📍 ¡Ubicación confirmada desde el mapa!');
  }
  closeMapPicker();
};

// ============================================================================
// ACCIONES DE DIRECCIONES / SUCURSALES EN MEMORIA
// ============================================================================
const openAddressModal = (addr = null, index = null) => {
  editingAddrIndex.value = index;
  if (addr) {
    Object.assign(addrForm, addr);
  } else {
    Object.assign(addrForm, {
      id: null,
      alias: '',
      contact_phone: '',
      address: '',
      reference_address: '',
      latitude: null,
      longitude: null,
      is_default: form.addresses.length === 0
    });
  }
  showAddrModal.value = true;
};

const saveAddress = () => {
  if (!addrForm.alias || !addrForm.address) return showError('Alias y Dirección son obligatorios');
  if (addrForm.is_default) {
    form.addresses.forEach(a => a.is_default = false);
  }
  if (editingAddrIndex.value !== null) {
    form.addresses[editingAddrIndex.value] = { ...addrForm };
  } else {
    form.addresses.push({ ...addrForm });
  }
  showAddrModal.value = false;
};

const removeAddress = (idx) => {
  const addr = form.addresses[idx];
  if (addr.id) {
    deletedAddressIds.value.push(addr.id);
  }
  form.addresses.splice(idx, 1);
  if (form.addresses.length > 0 && !form.addresses.some(a => a.is_default)) {
    form.addresses[0].is_default = true;
  }
};

const setDefaultAddress = (idx) => {
  form.addresses.forEach((a, i) => a.is_default = (i === idx));
};

// Estrategia de doble intento (Fallback) por si falla el GPS de PC
const captureAddrGPS = () => {
  if (!navigator.geolocation) {
    showError('Tu dispositivo no soporta geolocalización.');
    return;
  }

  isCapturingGPS.value = true;

  const handleSuccess = (position, isHighAccuracy) => {
    addrForm.latitude = Number(position.coords.latitude.toFixed(8));
    addrForm.longitude = Number(position.coords.longitude.toFixed(8));
    addrForm.gps_coordinates = `${addrForm.latitude}, ${addrForm.longitude}`;

    isCapturingGPS.value = false;

    if (isHighAccuracy) {
      showSuccess('📍 GPS satelital capturado con alta precisión');
    } else {
      showSuccess('📍 Coordenada capturada por red/Wi-Fi');
    }
  };

  // INTENTO 1: Alta precisión (Satélite)
  navigator.geolocation.getCurrentPosition(
    (position) => handleSuccess(position, true),
    (error) => {
      if (error.code === error.TIMEOUT || error.code === error.POSITION_UNAVAILABLE) {
        // INTENTO 2: Respaldo por Wi-Fi (Instantáneo en PC/Oficinas)
        navigator.geolocation.getCurrentPosition(
          (positionFallback) => handleSuccess(positionFallback, false),
          (fallbackError) => {
            isCapturingGPS.value = false;
            showError('No se pudo leer el GPS. Te sugerimos usar la opción "🗺️ Ubicar en el Mapa".');
          },
          { enableHighAccuracy: false, timeout: 15000, maximumAge: 60000 }
        );
      } else if (error.code === error.PERMISSION_DENIED) {
        isCapturingGPS.value = false;
        showError('Permiso de ubicación denegado en el navegador.');
      } else {
        isCapturingGPS.value = false;
        showError('Error al leer el sensor GPS.');
      }
    },
    { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
  );
};

// Carga de direcciones por separado desde la API
const loadCustomerAddresses = async (customerId) => {
  try {
    const res = await apiClient.get(`/customer_addresses?customer_id=${customerId}`);
    form.addresses = Array.isArray(res.data) ? res.data : (res.data.data || []);
  } catch (e) {
    console.error("Error cargando sucursales/direcciones:", e);
    form.addresses = [];
  }
};

const initSignature = () => {
  nextTick(() => {
    const canvas = document.getElementById('signature-pad');
    if (canvas) {
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      canvas.width = canvas.offsetWidth * ratio;
      canvas.height = canvas.offsetHeight * ratio;
      canvas.getContext("2d").scale(ratio, ratio);
      signaturePad.value = new SignaturePad(canvas, { penColor: 'rgb(15, 23, 42)' });
    }
  });
};

const clearSignature = () => signaturePad.value?.clear();

watch(() => activeTab.value, (tab) => {
  if (tab === 'commercial') initSignature();
});

watch(() => props.show, async (val) => {
  if (val) {
    activeTab.value = 'general';
    deletedAddressIds.value = [];

    if (props.applicationToEdit && Object.keys(props.applicationToEdit).length > 0) {
      Object.assign(form, props.applicationToEdit);
      if (props.applicationToEdit.id) {
        await loadCustomerAddresses(props.applicationToEdit.id);
      } else {
        form.addresses = props.applicationToEdit.addresses || [];
      }
    } else {
      Object.assign(form, {
        id: null, customer_type: 'B2B_BUSINESS', business_name: '', business_sector: '',
        first_name: '', last_name: '', mobile_phone: '', work_phone: '', email: '',
        identity_document: '', is_taxpayer: false, tax_registration_number: '',
        photo_url: '', document_front_url: '', document_back_url: '',
        addresses: [], commission_rate: 0.00, credit_limit: 0.00, signature_base64: null
      });
      clearSignature();
    }
  }
});

const nextTab = () => {
  const currentIndex = tabs.findIndex(tab => tab.id === activeTab.value);
  if (currentIndex < tabs.length - 1) activeTab.value = tabs[currentIndex + 1].id;
};

// Sincronizar direcciones en el Backend
const syncAddresses = async (customerId) => {
  const promises = [];

  if (deletedAddressIds.value.length > 0) {
    deletedAddressIds.value.forEach(id => {
      promises.push(apiClient.delete(`/customer_addresses/${id}`));
    });
  }

  form.addresses.forEach(addr => {
    const payloadAddr = {
      ...addr,
      customer_id: customerId,
      is_default: addr.is_default ? 1 : 0
    };

    if (addr.id) {
      promises.push(apiClient.put(`/customer_addresses/${addr.id}`, payloadAddr));
    } else {
      promises.push(apiClient.post('/customer_addresses', payloadAddr));
    }
  });

  await Promise.all(promises);
};

const handleSubmit = async () => {
  if (!form.first_name || !form.last_name || !form.mobile_phone || !form.identity_document) {
    return showError('Completa nombres, WhatsApp y documento de identidad obligatoriamente.');
  }

  isSaving.value = true;
  try {
    const user = SecureStorage.getItem('llevo_user');
    const { addresses, ...customerPayload } = form;

    const payload = {
      ...customerPayload,
      company_id: user?.company || 1,
      is_taxpayer: form.is_taxpayer ? 1 : 0
    };

    if (signaturePad.value && !signaturePad.value.isEmpty()) {
      payload.signature_base64 = signaturePad.value.toDataURL();
    }

    let customerId = form.id;

    if (form.id) {
      await apiClient.put(`/customers/${form.id}`, payload);
    } else {
      const res = await apiClient.post('/customers', payload);
      customerId = res.data?.id || res.data?.data?.id;
    }

    if (customerId) {
      await syncAddresses(customerId);
    }

    showSuccess('Comercio/Cliente y sus sucursales guardados correctamente');
    emit('saved');
    emit('close');
  } catch (error) {
    console.error("Error en guardado de cliente/sucursal:", error);
    showError('Error al guardar el expediente o sus direcciones en el servidor.');
  } finally {
    isSaving.value = false;
  }
};
</script>

<style scoped>
@keyframes fade-in { from { opacity: 0; transform: scale(0.98); } to { opacity: 1; transform: scale(1); } }
.animate-fade-in { animation: fade-in 0.2s ease-out forwards; }
.custom-scrollbar::-webkit-scrollbar { width: 4px; }
.custom-scrollbar::-webkit-scrollbar-thumb { background-color: #cbd5e1; border-radius: 20px; }
</style>